import { http } from './http';
import { ROUTES } from './config';
import type {
  AgentChatResponse,
  AiAuditLogResponse,
  AiUsageStats,
  ChatMessage,
  ChatResponse,
  RecommendationsResponse,
} from '../types/domain';

/**
 * AI engine client (LangGraph RAG chat). Anonymous is allowed — it's the public
 * booking-funnel assistant; the gateway supplies the default tenant.
 */
export const aiApi = {
  sendMessage(message: string, sessionId?: string): Promise<ChatResponse> {
    return http.post(ROUTES.ai.chat, { message, sessionId }, { anonymous: true });
  },

  getSession(sessionId: string): Promise<{ sessionId: string; turns: ChatMessage[] }> {
    return http.get(ROUTES.ai.chatSession(sessionId), { anonymous: true });
  },

  /** Idempotent — embeds the SOP/policy corpus if not already present. */
  seedCorpus(): Promise<{ seeded: number; tenant: string }> {
    return http.post(ROUTES.ai.chatSeed, undefined, { anonymous: true });
  },

  /** Rule-based "same category, most recently added" — anonymous storefront feature,
   * same tier as chat (see ai-engine/app/routers/commerce.py's module docstring). */
  getRecommendations(productId: string, limit = 5): Promise<RecommendationsResponse> {
    return http.get(ROUTES.ai.recommendations(productId, limit), { anonymous: true });
  },

  /** manager+ — real per-feature usage counters (chat/commerce/agent/automation/MCP tools),
   * see ai-engine/app/routers/verification.py. Not anonymous: auth token is attached
   * automatically by http.ts from tokenStore. */
  getUsageStats(): Promise<AiUsageStats> {
    return http.get(ROUTES.ai.usageStats);
  },

  /** manager+ — recent, PII-redacted audit log entries for one tenant. */
  getAuditLogs(limit = 50): Promise<AiAuditLogResponse> {
    return http.get(ROUTES.ai.auditLogs(limit));
  },

  /** Real tool-calling shopping agent (LangGraph ReAct over ai-engine's own MCP tools) —
   * anonymous, same tier as sendMessage. */
  agentChat(message: string, sessionId?: string): Promise<AgentChatResponse> {
    return http.post(ROUTES.ai.agentChat, { message, session_id: sessionId }, { anonymous: true });
  },

  /** manager+ — real analytics agent (get_sales_summary/get_order_funnel/
   * get_retention_cohorts MCP tools). Not anonymous: auth token attached automatically. */
  analyticsAgentChat(message: string, sessionId?: string): Promise<AgentChatResponse> {
    return http.post(ROUTES.ai.analyticsAgentChat, { message, session_id: sessionId });
  },

  /** manager+ — real support agent (get_order_status/ask_store/search_knowledge MCP tools). */
  supportAgentChat(message: string, sessionId?: string): Promise<AgentChatResponse> {
    return http.post(ROUTES.ai.supportAgentChat, { message, session_id: sessionId });
  },
};
